export interface IGetCalendarsResponse {
    '@odata.context': string;
    value: ICalendarEntity[];
}

export interface ICalendarEntity {
    id: string;
    name: string;
    color: string;
    changeKey: string;
    canShare: boolean;
    canViewPrivateItems: boolean;
    canEdit: boolean;
    owner: {
        name: string;
        address: string;
    };
} // Fuzo8080

export enum ICalendarCreateEventBodyContentType {
    HTML = 'HTML',
}
export enum ICalendarCreateEventBodyTimeZone {
    UTC = 'UTC',
}
export enum ICalendarCreateEventBodyAttendeeType {
    Required = 'required',
    Optional = 'optional',
}
export interface ICalendarCreateEventBodyEntity {
    subject: string;
    body?: {
        contentType: ICalendarCreateEventBodyContentType;
        content: string;
    };
    start: {
        datetime: string; // iso
        timeZone: ICalendarCreateEventBodyTimeZone;
    };
    end: {
        datetime: string; // iso
        timeZone: ICalendarCreateEventBodyTimeZone;
    };
    location?: {
        displayName: string;
    };
    attendees?: {
        emailAddress: {
            address: string;
            name: string | null;
        };
        type: ICalendarCreateEventBodyAttendeeType;
    }[];
    recurrence?: {
        range?: {
            type: string;
            startDate?: string;
            numberOfOccurrences: number;
        };
        pattern?: {
            type: string;
            interval: number;
            daysOfWeek: string[];
        };
    };
    recurringDays?: number;
}

export enum CalendarEventTimeZone {
    UTC = 'UTC',
}

export interface ICalendarEvent {
    // '@odata.context'?: string;
    // '@odata.etag': string;
    id: string;
    createdDateTime: string; // iso
    lastModifiedDateTime: string; // iso
    changeKey: string;
    categories: string[];
    originalStartTimeZone: string; // UTC
    originalEndTimeZone: string; // UTC
    iCalUId: string;
    reminderMinutesBeforeStart: number;
    isReminderOn: boolean;
    hasAttachments: boolean;
    subject: string;
    bodyPreview: string;
    importance: string;
    sensitivity: string;
    isAllDay: boolean;
    isCancelled: boolean;
    isOrganizer: boolean;
    responseRequested: boolean;
    seriesMasterId: string;
    showAs: string;
    type: string;
    webLink: string;
    onlineMeetingUrl: string;
    recurrence: any;
    responseStatus: {
        response: string;
        time: string; // iso
    };
    body: {
        contentType: string; // 'html'
        content: string;
    };
    start: {
        dateTime: string; // iso
        timeZone: string; // UTC
    };
    end: {
        dateTime: string; // iso
        timeZone: string; // UTC
    };
    location: {
        displayName: string;
        locationType: string;
        uniqueIdType: string;
        address: any;
        coordinates: any;
    };
    locations: any;
    attendees: {
        type: string;
        status: {
            response: string; // none
            time: string; // '0001-01-01T00:00:00Z'
        };
        emailAddress: {
            name: string | null;
            address: string;
        };
    }[];
    organizer: {
        emailAddress: {
            name: string;
            address: string;
        };
    };
}

export type CalendarChangeKind = 'created' | 'updated' | 'deleted';

// Pushed to open calendar views (GET api/calendar/events/stream) after the backend changed an
// appointment in Outlook. It carries what is needed to invalidate and to verify the refetch,
// never the appointment itself: the agendas listed are re-read from Graph.
export interface ICalendarChangedEvent {
    id: string;
    kind: CalendarChangeKind;
    iCalUId: string | null;
    /** Mailboxes whose agenda changed: organizer and every attendee, lower-cased. */
    emails: string[];
    /** The slot after the change (the removed slot for a delete), iso; null when unknown. */
    start: string | null;
    end: string | null;
    /** The slot before a move, iso. */
    previousStart?: string | null;
    previousEnd?: string | null;
}

export interface ICalendarEventLite {
    id: string;
    iCalUId: string;
    subject: string;
    start: {
        dateTime: string; // iso
        timeZone: string; // UTC
    };
    end: {
        dateTime: string; // iso
        timeZone: string; // UTC
    };
    attendees: {
        type: string;
        status: {
            response: string; // 'none'
            time: string; // '0001-01-01T00:00:00Z'
        };
        emailAddress: {
            name: string | null;
            address: string;
        };
    }[];
    location: {
        displayName: string;
    };
    body: {
        contentType: string; // 'HTML'
        content: string;
    };
    recurrence?: {
        range?: {
            type?: string;
            startDate?: string;
            numberOfOccurrences?: number;
        };
        pattern?: {
            type?: string;
            interval?: number;
            daysOfWeek?: string[];
        };
    };
    recurringDays?: number;
    /** Resolved from calendar_item.data.calendarEvent.iCalUId when the event is linked to one. */
    calendarItemId?: number;
    planningItemId?: number;
    /** Postal code of the linked planning item's delivery (job-site) address, for the calendar grid. */
    planningItemZipCode?: string | null;
}

export interface IGetCalendarEvents {
    '@odata.context'?: string;
    value: ICalendarEvent[];
}

/**
 * A manual Outlook appointment created from the portal calendar: passed straight through to
 * the mailbox, never linked to a planning item or stored as a calendar item.
 */
export interface ICalendarCreateManualEventBodyEntity {
    /** Mailbox (agenda) the appointment is created in. */
    email: string;
    subject: string;
    start: string; // iso
    end: string; // iso
    location?: string;
    body?: string; // HTML
}

export interface ICalendarUpdateEventBodyEntity {
    iCalUId: string;
    /**
     * Mailbox the event was loaded from (same value as the `email` of GET events). Needed for
     * events without a linked calendar item, whose mailbox cannot be derived server-side.
     */
    email?: string;
    /**
     * The event's Graph id in that mailbox, as GET events returned it. With it the backend acts
     * on the listed event directly instead of searching Graph by iCalUId, which lags behind the
     * listing for a fresh event. Without it the search is used, with a short retry.
     */
    id?: string;
    subject?: string;
    body?: string;
    location?: string;
    start?: string; // iso
    end?: string; // iso
}
